import { motion } from "framer-motion";
import { fadeInUp, staggerContainer, viewportConfig } from "@/lib/motion";
import { useParallax } from "@/lib/useParallax";
import { Cpu, Thermometer, FlaskConical, ScanLine, Layers, ShieldCheck } from "lucide-react";

const ROBOT_IMG = "https://images.pexels.com/photos/19233057/pexels-photo-19233057.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

const CAPABILITIES = [
  { icon: Layers, title: "Cell-to-Pack Assembly", desc: "Planned automated cell sorting, matching, and welding with sub-millimeter precision across cylindrical, prismatic, and pouch formats." },
  { icon: Cpu, title: "BMS Engineering", desc: "In-house Battery Management System design under development, with real-time cell balancing, SOC/SOH estimation, and CAN/Modbus communication protocols." },
  { icon: Thermometer, title: "Thermal Management", desc: "Active liquid and air cooling architectures being validated through CFD simulation and thermal chamber testing across extreme operating envelopes." },
  { icon: FlaskConical, title: "Cell Chemistry R&D", desc: "Research partnerships for NMC, LFP, and next-gen silicon anode chemistries optimized for Indian climate and use-case demands." },
  { icon: ScanLine, title: "Automated QC Lines", desc: "Planned end-of-line testing with automated impedance analysis, capacity verification, insulation resistance testing, and vision inspection systems." },
  { icon: ShieldCheck, title: "Compliance & Certification", desc: "Production being architected to AIS-156, BIS, IEC 62619, UN 38.3, and customer-specific validation protocols for domestic and export markets." },
];

export default function ManufacturingSection() {
  const { ref, y } = useParallax(-0.15);
  return (
    <section ref={ref} id="manufacturing" data-testid="manufacturing-section" className="relative py-10 md:py-14 lg:py-20 bg-[#040D1C] noise-overlay overflow-hidden">
      <div className="relative z-10 max-w-7xl mx-auto px-5 sm:px-6 lg:px-8">
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={viewportConfig}
          variants={fadeInUp}
          className="mb-4"
        >
          <span className="text-xs uppercase tracking-[0.3em] text-[#1E96FC]">
            // Manufacturing & Engineering
          </span>
        </motion.div>

        <div className="mb-10 md:mb-16">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={viewportConfig}
            variants={fadeInUp}
          >
            <h2 className="font-display text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold uppercase text-slate-100 leading-tight mb-4 md:mb-5">
              <span className="block">Where precision meets</span>
              <span className="block text-[#1E96FC]">production scale</span>
            </h2>
            <p className="text-slate-400 leading-relaxed text-sm max-w-2xl">
              Our manufacturing infrastructure is being designed for repeatability and traceability at scale.
              <br />
              Every planned process — from incoming cell inspection to final pack validation — will follow a documented, auditable workflow.
            </p>
          </motion.div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-12 gap-6 md:gap-8">
          {/* Image Column */}
          <motion.div
            initial="hidden" whileInView="visible" viewport={viewportConfig} variants={fadeInUp}
            className="md:col-span-4"
          >
            <div className="relative rounded-sm overflow-hidden h-60 md:h-full md:min-h-[300px]">
              <motion.img style={{ y }} src={ROBOT_IMG} alt="Manufacturing" className="w-full h-[110%] object-cover will-change-transform" />
              <div className="absolute inset-0 bg-gradient-to-t from-[#061428] via-[#003771]/40 to-transparent" />
              <div className="absolute top-4 left-4">
                <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-sm bg-black/60 backdrop-blur-sm border border-[#1E96FC]/30 text-[9px] md:text-[10px] font-semibold uppercase tracking-[0.2em] text-white">
                  <span className="w-1.5 h-1.5 rounded-full bg-[#1E96FC] animate-pulse" />
                  Facility Under Construction
                </span>
              </div>
            </div>
          </motion.div>

          {/* Capabilities Grid */}
          <motion.div
            initial="hidden" whileInView="visible" viewport={viewportConfig} variants={staggerContainer}
            className="md:col-span-8 grid grid-cols-1 sm:grid-cols-2 gap-4"
          >
            {CAPABILITIES.map((cap, i) => (
              <motion.div
                key={i}
                variants={fadeInUp}
                className="glass-card rounded-sm p-6 group hover:bg-[#003771]/20 transition-all duration-300"
              >
                <cap.icon className="w-5 h-5 text-[#1E96FC] mb-3" />
                <h4 className="text-base font-bold uppercase text-slate-200 mb-2 tracking-wide">
                  {cap.title}
                </h4>
                <p className="text-xs text-slate-400 leading-relaxed">
                  {cap.desc}
                </p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </div>
    </section>
  );
}
